import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import UserContact, { UserContactSkeleton, validateUserContact } from './UserContact';
import { useJspVariables, useAuth, useLoader, useReferenceData, useDebugRender } from '../hooks';
import { IceIcon, PageContainer, PageHeader } from '../components';
import Button from '@mui/material/Button';
import { getLabelFromJspVariables } from '../common/JspUtil';

const EMPTY_USER_PROFILE = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCode: '',
    phoneStatus: '',
    altPhone: '',
    altPhoneCode: '',
    altPhoneStatus: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    countryCode: '',
    jurisdiction: '',
    employeeId: '',
    companyId: '',
    companyName: '',
    tfaMethod: '',
    addressStatus: ''
};

const PageConfirmContact = () => {
    useDebugRender('PageConfirmContact');
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { jspVariables, launcher, panelStyle } = useJspVariables();

    const { authenticatedUser, isAuthenticated, ssoEnvironment } = useAuth();
    const { countries, countryByCode } = useReferenceData();
    const { showLoader, hideLoader } = useLoader();

    const sourcePage = location?.state?.sourcePage || '';
    const fromGlobalId = location?.state?.fromGlobalId || false;
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});
    const [userProfile, setUserProfile] = useState(null);

    const getAreacodes = () => {
        let areacodes = [];
        areacodes.push({ countryCode: 'US', countryName: 'United States', phoneCode: '+1', displayCode: 'US +1', code: 'US+1' });
        if (countries && countries.length > 0) {
            for (let country of [...countries].sort((x, y) => x.countryCode < y.countryCode ? -1 : 1)) {
                if (country.countryCode != 'US') {
                    areacodes.push({ ...country, displayCode: country.countryCode + ' ' + country.phoneCode, code: country.countryCode + country.phoneCode });
                }
            }
        }
        return areacodes;
    };

    const validate = () => {
        setAlert(null);
        const fieldErrors = validateUserContact(userProfile, ssoEnvironment, getAreacodes(), countryByCode);
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const confirmUserContact = async () => {
        if (!validate()) return;
        const address = {
            ...userProfile,
            userName: authenticatedUser.user,
            userId: authenticatedUser.user,
            appKey: authenticatedUser.app,
            confirmStatus: 'Y'
        };
        showLoader('Confirming contact details...');
        try {
            const resp = await actions.saveUserProfile(address);
            setUserProfile(resp.data);
            dispatch({ type: 'USER_CONTACT_VERIFIED', data: true });
            setAlert({ message: getLabelFromJspVariables('alert_contact_confirmed'), severity: 'success' });
            setTimeout(() => navigate(sourcePage || constants.ROUTE_PAGE_AUTHENTICATED), 1500);
        } catch (resp) {
            if (resp.code == errorCodes.ERR_REQUIRES_OTP_ENTERED_AUTH) {
                dispatch(actions.logoutUserToken(authenticatedUser.user, false, false));
            } else {
                setAlert({ message: resp.message || getLabelFromJspVariables('err_contact_save_failed'), severity: 'error' });
            }
        } finally {
            hideLoader();
        }
    };

    const skipConfirmation = async (canSkip, fromGlobal) => {
        if (fromGlobal && sourcePage) {
            dispatch({ type: 'USER_CONTACT_VERIFIED', data: true });
            navigate(sourcePage);
            return;
        }
        if (canSkip) {
            const address = {
                ...userProfile,
                userName: authenticatedUser.user,
                userId: authenticatedUser.user,
                appKey: authenticatedUser.app,
                confirmStatus: 'N'
            };
            showLoader('Saving...');
            try {
                const resp = await actions.saveUserProfile(address);
                // Skip short-circuits server side and answers with an empty body, which would
                // blank the form; adopt the saved address only when one comes back.
                if (resp.data && typeof resp.data === 'object') setUserProfile(resp.data);
                dispatch({ type: 'USER_CONTACT_VERIFIED', data: true });
                navigate(sourcePage || constants.ROUTE_PAGE_AUTHENTICATED);
            } catch (resp) {
                setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
            } finally {
                hideLoader();
            }
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            dispatch(actions.checkPermissions({ page: constants.ROUTE_PAGE_CONFIRM_CONTACT }));
        }
    }, []);

    useEffect(() => {
        if (authenticatedUser && authenticatedUser.user && !userProfile) {
            if (!countries || countries.length === 0) {
                dispatch(actions.getReferenceData(authenticatedUser.user, authenticatedUser.app));
            }
            actions.loadUserProfile(authenticatedUser.user, authenticatedUser.app).then(resp => {
                setUserProfile(resp.data);
                if (resp.data.addressStatus == 'T') {
                    dispatch({ type: 'USER_CONTACT_VERIFIED', data: true });
                    navigate(sourcePage || constants.ROUTE_PAGE_AUTHENTICATED);
                }
            }).catch(err => {
                if (Number(err.code) === errorCodes.ERR_INVALID_ARGS) {
                    // A newly registered user may not have an address row yet. Render an
                    // empty form so their first contact record can be created.
                    setUserProfile({ ...EMPTY_USER_PROFILE });
                } else {
                    setAlert({ message: err.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
                }
            });
        }
    }, [authenticatedUser]);

    const canSkip = authenticatedUser && authenticatedUser.graceCount > 0;
    const fromGlobal = fromGlobalId || (jspVariables.jspPage === 'REGISTER_GID');

    return (
        <PageContainer id="pageConfirmContact" dataAutomationId="page_confirm_contact" md={10} lg={8}>
            <Box style={panelStyle}>
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="confirm_contact_header">{getLabelFromJspVariables('title_confirm_contact_info')}</PageHeader>
                {alert && <Alert id="pageConfirmContactAlert" data-automation-id="page_confirm_contact_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                {userProfile ?
                    <UserContact
                        userProfile={userProfile}
                        onProfileChange={setUserProfile}
                        readOnly={false}
                        authenticatedUser={authenticatedUser}
                        launcher={launcher}
                        countries={countries}
                        countryByCode={countryByCode}
                        errors={errors}
                        onClearError={clearFieldError}
                    />
                    :
                    <UserContactSkeleton />
                }
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
                    <Button variant="contained" onClick={confirmUserContact}
                        data-automation-id="confirm_contact_confirm_btn">
                        {getLabelFromJspVariables('action_confirm')}
                    </Button>
                    {(canSkip || fromGlobal) &&
                        <Button variant="contained"
                            onClick={() => skipConfirmation(canSkip, fromGlobal)}
                            data-automation-id="confirm_contact_skip_btn">
                            {getLabelFromJspVariables('action_skip')}
                        </Button>
                    }
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageConfirmContact;
